import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import type { NextPageContext } from 'next'
import { useRouter } from 'next/router'
import { Stack, Typography } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import { TextFieldController } from 'settle-ui/form/TextFieldController/TextFieldController'
import { useSnackbars } from 'settle-ui/hooks/useSnackbars/useSnackbars'
import { ExternalAppLayout } from 'routes/components/ExternalAppLayout/ExternalAppLayout'
import { EMAIL_PATTERN } from 'lib/patterns'
import { withPageErrorRecovery } from 'lib/hocs/withPageErrorRecovery/withPageErrorRecovery'
import { useDeleteTokenCookieOnMount } from '../../lib/hooks/useDeleteTokenCookieOnMount/useDeleteTokenCookieOnMount'
import * as $ from './ForgotPasswordPage.styled'
import { ForgotPasswordPage_newPasswordRequestDocument } from './ForgotPasswordPage.graphql'

interface ForgotPasswordForm {
  email: string
}

interface ForgotPasswordPageProps {
  initialEmail?: string | null
}

export const ForgotPasswordPage = withPageErrorRecovery<ForgotPasswordPageProps>(({ initialEmail }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { showApolloOrTextError } = useSnackbars()
  const [newPasswordRequest, newPasswordRequestState] = useMutation(ForgotPasswordPage_newPasswordRequestDocument)
  const form = useForm<ForgotPasswordForm>({ defaultValues: { email: initialEmail || '' } })
  const emailFormValue = form.watch('email')

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await newPasswordRequest({ variables: values })
    } catch (err) {
      showApolloOrTextError(err)
    }
  })

  useEffect(() => {
    if ('email' in router.query) {
      const query = { ...router.query }

      delete query.email

      router.replace({ query }, undefined, { shallow: true })
    }
  }, [router])

  useDeleteTokenCookieOnMount()

  return (
    <ExternalAppLayout data-testid="forgot-password-page">
      {form.formState.isSubmitSuccessful ? (
        <$.Container maxWidth="xs" data-testid="submit-success-feedback">
          <$.Title>{t('checkEmail')}</$.Title>
          <Typography align="center" variant="body1" paragraph={true} color="primary">
            {t('weHaveJustSentYouAnEmailWithLink', { inputEmail: emailFormValue })}
          </Typography>
        </$.Container>
      ) : (
        <$.Container maxWidth="xs">
          <$.Title>{t('enterEmailToResetPassword')}</$.Title>
          <Stack component="form" onSubmit={handleSubmit} noValidate={true}>
            <TextFieldController
              name="email"
              type="email"
              size="large"
              control={form.control}
              label={t('label::input::email')}
              rules={{
                pattern: { value: EMAIL_PATTERN, message: t('emailIsIncorrect') },
                required: { value: true, message: t('emailCantBeEmpty') },
              }}
            />
            <$.Button type="submit" variant="primary" size="large" loading={newPasswordRequestState.loading}>
              {t('sendPasswordResetLink')}
            </$.Button>
          </Stack>
        </$.Container>
      )}
    </ExternalAppLayout>
  )
})

ForgotPasswordPage.getInitialProps = ({ query }: NextPageContext) => ({
  initialEmail: typeof query.email === 'string' ? query.email : null,
})
